import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { ActualResidenceFieldsValues, BaseChapterProps, ProfileFormValues } from '@/pages/UserAccount/Profile';
import { FormHasDirtyFields } from '@/pages/UserAccount/Profile/ui/ProfilePresentational';

import { SelectOption } from '@/shared/types';
import { FormSelect, FormSwitch, FormTextInput } from '@/shared/ui';

import { isCountryUkraine, isRegionKyiv, isRegionSevastopol } from '../model';

import BaseChapterTemplate from './BaseChapterTemplate/BaseChapterTemplate';

interface ActualResidenceProps extends BaseChapterProps {
  onSubmit: (values: ProfileFormValues) => void;
  optionsCountry: SelectOption[];
  optionsRegion: SelectOption[];
  optionsDistrict: SelectOption[];
  optionsCity: SelectOption[];
  optionsStreet: SelectOption[];
  setFormHasDirtyFields: (value: FormHasDirtyFields) => void;
  activeKey: Array<NonNullable<FormHasDirtyFields>>;
}

const actualResidenceFields = [
  'actualBuilding',
  'actualCity',
  'actualCountry',
  'actualDistrict',
  'actualApartment',
  'actualRegion',
  'actualStreet',
  'actualHouse',
  'actualAddressEqualsResidence',
] satisfies Array<keyof ActualResidenceFieldsValues>;

export const ActualResidence: FC<ActualResidenceProps> = ({
  onSubmit,
  onReset,
  optionsCity,
  optionsStreet,
  optionsCountry,
  optionsRegion,
  optionsDistrict,
  setFormHasDirtyFields,
  activeKey,
}) => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { dirtyFields },
  } = useFormContext<ProfileFormValues>();

  const actualAddressEqualsResidence = watch('actualAddressEqualsResidence');
  const actualCountry = watch('actualCountry');
  const actualRegion = watch('actualRegion');
  const actualDistrict = watch('actualDistrict');
  const actualCity = watch('actualCity');
  const actualStreet = watch('actualStreet');

  const isActualResidenceFieldsDirty = Object.keys(dirtyFields).some((key) =>
    actualResidenceFields.includes(key as keyof ActualResidenceFieldsValues),
  );

  useEffect(() => {
    if (!isActualResidenceFieldsDirty) return setFormHasDirtyFields(null);

    setFormHasDirtyFields('actual');
  }, [isActualResidenceFieldsDirty]);

  useEffect(() => {
    if (!activeKey.includes('actual') && isActualResidenceFieldsDirty) onReset();
  }, [activeKey]);

  const isUkraine = isCountryUkraine(actualCountry?.id ?? '');
  const isKyiv = isRegionKyiv(actualRegion?.id ?? '');
  const isSevastopol = isRegionSevastopol(actualRegion?.id ?? '');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <BaseChapterTemplate
        header={
          <FormSwitch
            control={control}
            name="actualAddressEqualsResidence"
            label="Адреса реєстрації співпадає з адресою проживання"
          />
        }
        fields={
          actualAddressEqualsResidence
            ? []
            : [
                <FormSelect
                  options={optionsCountry}
                  control={control}
                  name="actualCountry"
                  label="Країна фактичного проживання"
                  key="actualCountry"
                />,
                isUkraine ? (
                  <FormSelect
                    options={(optionsRegion ?? []) satisfies SelectOption[]}
                    control={control}
                    name="actualRegion"
                    label="Область фактичного проживання"
                    key="actuaRegion"
                    disabled={!optionsRegion?.length || !actualCountry}
                  />
                ) : null,
                isUkraine ? (
                  <FormSelect
                    options={(optionsDistrict ?? []) satisfies SelectOption[]}
                    control={control}
                    name="actualDistrict"
                    label="Район фактичного проживання"
                    key="actualDistrict"
                    disabled={!optionsDistrict?.length || !actualRegion || isKyiv || isSevastopol}
                  />
                ) : null,
                isUkraine ? (
                  <FormSelect
                    options={(isKyiv || isSevastopol ? optionsRegion : optionsCity) satisfies SelectOption[]}
                    control={control}
                    name="actualCity"
                    label="Населений пункт фактичного проживання"
                    key="actualCity"
                    disabled={!optionsCity?.length || !actualDistrict || isKyiv || isSevastopol}
                  />
                ) : (
                  <FormTextInput
                    control={control}
                    name="actualCity"
                    label="Населений пункт фактичного проживання"
                    placeholder="Вкажіть населений пункт"
                    key="actualCity"
                    disabled={!actualCountry}
                  />
                ),
                isUkraine ? (
                  <FormSelect
                    options={(optionsStreet ?? []) satisfies SelectOption[]}
                    control={control}
                    name="actualStreet"
                    label="Вулиця фактичного проживання"
                    key="actualStreet"
                    disabled={!optionsStreet.length || !actualCity}
                  />
                ) : (
                  <FormTextInput
                    control={control}
                    name="actualStreet"
                    label="Вулиця фактичного проживання"
                    placeholder="Вкажіть вулицю"
                    key="actualStreet"
                    disabled={!actualCountry}
                  />
                ),

                <FormTextInput
                  control={control}
                  label="Номер будинку фактичного проживання"
                  placeholder="Вкажіть будинок"
                  name="actualHouse"
                  key="actualHouse"
                  disabled={!actualStreet || (!isUkraine && !actualCity && !actualStreet)}
                />,
                <FormTextInput
                  control={control}
                  name="actualBuilding"
                  label="Номер корпусу фактичного проживання"
                  placeholder="Вкажіть корпус"
                  key="actualBuilding"
                  disabled={!actualStreet || (!isUkraine && !actualCity && !actualStreet)}
                />,
                <FormTextInput
                  control={control}
                  name="actualApartment"
                  label="Номер квартири фактичного проживання"
                  placeholder="Вкажіть номер квартири"
                  key="actualApartment"
                  disabled={!actualStreet || (!isUkraine && !actualCity && !actualStreet)}
                />,
              ]
        }
        isDisabledSubmitAction={isActualResidenceFieldsDirty}
        onReset={onReset}
      />
    </form>
  );
};
